import { useEffect, useMemo, useRef, useState } from 'react';

import { Dialog, Select } from 'components';
import {
	getQueryError,
	handleApiResponse,
	useAutoMapFirstFileColumnsMutation,
	useBootstrapHealthsystemMutation,
} from 'store';
import { useToast } from 'context';
import type { FirstFileIntraopField, FirstFileIntraopFieldDefinition } from 'models';

import {
	buildFacilities,
	compactMappings,
	emptyMappings,
	mergeAutoMappings,
	optionalIntraopFields,
	requiredIntraopFields,
	type ParsedFirstFile,
} from './firstFileConfiguration';

interface Props {
	file: File;
	parsed: ParsedFirstFile;
	healthsystemId: number;
	placeholderFacilityId: number;
	onCancel: () => void;
	onConfigured: (primaryFacilityId: number) => Promise<void>;
}

interface HeaderOption {
	label: string;
	value: string;
}

export function FirstFileConfigurationDialog(props: Props) {
	const { file, parsed, healthsystemId, placeholderFacilityId, onCancel, onConfigured } = props;
	const toast = useToast();
	const [step, setStep] = useState<1 | 2>(1);
	const [mappings, setMappings] = useState(emptyMappings);
	const manuallyEdited = useRef(new Set<FirstFileIntraopField>());
	const autoMapRequested = useRef(false);
	const [autoMapUnavailable, setAutoMapUnavailable] = useState(false);
	const [autoMapFirstFileColumns, { isLoading: isAutoMapping }] = useAutoMapFirstFileColumnsMutation();
	const [bootstrap, { isLoading }] = useBootstrapHealthsystemMutation();
	const options = parsed.headers.map((header) => ({ label: header, value: header }));
	const facilities = useMemo(() => buildFacilities(parsed, mappings), [parsed, mappings]);
	const selectedMappings = Object.values(mappings).filter(Boolean);
	const uniqueMappings = new Set(selectedMappings);
	const mappingComplete =
		requiredIntraopFields.every((field) => mappings[field.key]) && uniqueMappings.size === selectedMappings.length;

	useEffect(() => {
		if (autoMapRequested.current) return;
		autoMapRequested.current = true;
		void autoMapFirstFileColumns({
			healthsystem_id: healthsystemId,
			placeholder_facility_id: placeholderFacilityId,
			headers: parsed.headers,
		}).then((response) => {
			if ('data' in response) {
				setMappings((current) => mergeAutoMappings(current, response.data.mappings, manuallyEdited.current));
				return;
			}
			setAutoMapUnavailable(true);
		});
	}, [autoMapFirstFileColumns, healthsystemId, parsed.headers, placeholderFacilityId]);

	const updateMapping = (key: FirstFileIntraopField, option: HeaderOption | null) => {
		manuallyEdited.current.add(key);
		setMappings((current) => ({ ...current, [key]: option?.value ?? '' }));
	};

	const configure = async () => {
		if (!mappingComplete || !facilities.length) return;
		const response = await bootstrap({
			healthsystem_id: healthsystemId,
			placeholder_facility_id: placeholderFacilityId,
			field_mappings: compactMappings(mappings),
			facilities,
		});
		handleApiResponse(response, {
			success: async (payload) => {
				await onConfigured(payload.primary_facility_id);
			},
			error: (error) => toast.createToast({ title: getQueryError(error), variant: 'error' }),
		});
	};

	const renderMapping = (field: FirstFileIntraopFieldDefinition) => {
		const usedByOtherField = new Set(
			Object.entries(mappings)
				.filter(([key]) => key !== field.key)
				.map(([, value]) => value)
		);
		return (
			<div
				key={field.key}
				className='grid grid-cols-[minmax(0,1fr)_3rem_minmax(0,1fr)] items-center gap-x-4 px-5 py-4 border-b border-bluegray-100 last:border-b-0'
			>
				<div className='min-w-0'>
					<Select<HeaderOption>
						label={`Header for ${field.label}`}
						hideLabel
						options={options.filter((option) => !usedByOtherField.has(option.value))}
						value={options.find((option) => option.value === mappings[field.key])}
						disabled={field.key === 'facility'}
						onChange={(option) => updateMapping(field.key, option)}
						reactSelectProps={{ isClearable: !field.required }}
					/>
					<p className='text-p3 text-gray-500 mt-1.5 truncate'>
						Samples: {parsed.samples[mappings[field.key]]?.join(', ') || 'Select a header'}
					</p>
				</div>

				<span className='material-symbol text-bluegray-300 text-center' aria-hidden='true'>
					arrow_forward
				</span>

				<div>
					<p className='font-semibold text-p2 text-bluegray-800'>{field.label}</p>
					<p className='text-p3 text-gray-500 mt-1'>{field.description}</p>
					<p className='text-p3 text-gray-500 mt-1'>{field.required ? 'Required by Merlin' : 'Optional'}</p>
				</div>
			</div>
		);
	};
	const autoMapStatus = isAutoMapping ? (
		<p className='flex items-center gap-2 text-p2 text-blue-600' role='status'>
			<span className='material-symbol animate-spin' aria-hidden='true'>
				progress_activity
			</span>
			Auto-mapping columns with Merlin AI…
		</p>
	) : autoMapUnavailable ? (
		<p className='text-p2 text-gray-600' role='status'>
			Merlin AI auto-mapping is unavailable. You can continue by mapping the columns manually.
		</p>
	) : null;

	return (
		<Dialog
			open
			onOpenChange={(open) => !open && !isLoading && onCancel()}
			trigger={null}
			sizeX='lg'
			title='Configure Your First Intraop File'
			subtitle={`Step ${step} of 2 — ${step === 1 ? 'Identify Facilities' : 'Map Intraop Fields'}`}
			disableFirstButton={step === 1 ? !mappings.facility || !facilities.length : !mappingComplete}
			actionButtons={
				step === 1
					? [
							{ children: 'Continue', onClick: () => setStep(2) },
							{ children: 'Cancel', onClick: onCancel },
					  ]
					: [
							{ children: 'Configure & Upload', onClick: configure, isWorking: isLoading },
							{ children: 'Back', onClick: () => setStep(1), disabled: isLoading },
					  ]
			}
		>
			{step === 1 ? (
				<div className='space-y-5'>
					<p className='text-p2 text-gray-600'>
						Select the column that identifies the facility for each row in {file.name}.
					</p>
					{autoMapStatus}
					<Select<HeaderOption>
						label='Facility Column'
						options={options}
						value={options.find((option) => option.value === mappings.facility)}
						onChange={(option) => updateMapping('facility', option)}
					/>
					<div>
						<p className='font-semibold mb-2'>Facilities discovered ({facilities.length})</p>
						<div className='max-h-52 overflow-y-auto border rounded p-3 space-y-2'>
							{facilities.map((facility, index) => (
								<div key={facility.name} className='flex justify-between text-p2'>
									<span>{index === 0 ? `${facility.name} (replaces [Unknown])` : facility.name}</span>
									<span className='text-gray-500'>{facility.rooms.length} rooms</span>
								</div>
							))}
						</div>
					</div>
				</div>
			) : (
				<div className='flex flex-col h-[60vh] max-h-[40rem]'>
					<p className='text-p2 text-gray-600 mb-4'>
						Match each header from your Health System file to the field Merlin needs.
					</p>
					<div className='mb-4'>{autoMapStatus}</div>

					<div className='grid grid-cols-2 items-stretch overflow-hidden bg-bluegray-50 border border-bluegray-100 rounded-t'>
						<p className='flex items-center font-semibold text-p2 text-bluegray-700 px-5 py-3'>Your Health System File</p>
						<p className='flex items-center justify-center font-semibold text-p2 text-white text-center bg-blue-500 px-5 py-3'>
							What Merlin Needs
						</p>
					</div>

					<div className='flex-1 overflow-y-auto border-x border-b border-bluegray-100 rounded-b'>
						<div className='px-5 py-2 bg-bluegray-50 border-b border-bluegray-100'>
							<p className='font-semibold text-p3 uppercase text-bluegray-600'>Required Fields</p>
						</div>
						{requiredIntraopFields.map(renderMapping)}
						<div className='px-5 py-2 bg-bluegray-50 border-y border-bluegray-100'>
							<p className='font-semibold text-p3 uppercase text-bluegray-600'>Optional Fields</p>
						</div>
						{optionalIntraopFields.map(renderMapping)}
					</div>
				</div>
			)}
		</Dialog>
	);
}
